import { LinkOutlined } from '@ant-design/icons';
import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link, request } from '@umijs/max';
import React from 'react';
import { AvatarDropdown, AvatarName, Footer, Question } from '@/components';
import defaultSettings from '../config/defaultSettings';
import { requestConfig } from './requestConfig';
import '@ant-design/v5-patch-for-react-19';
import {getLogin} from "@/services/letter-backend/userController";
import CustomFooter from "@/components/CustomFooter";
const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

/** 后台菜单 path → featureKey 映射，用于按权限过滤菜单 */
const PATH_FEATURE_MAP: Record<string, string> = {
  '/user/user': 'agent:user',
  '/user/agent': 'agent:proxy',
  '/goods/product': 'product:manage',
  '/goods/feature': 'feature:market',
  '/orders/anonymous': 'order:anonymous',
  '/orders/phone': 'order:phone',
  '/orders/verify': 'order:verify',
  '/finance/top/up': 'finance:topuprecords',
  '/finance/order': 'finance:orders',
  '/notice': 'notice:manage',
  '/black/list': 'blacklist:manage',
  '/update/list': 'update:manage',
  '/system/config': 'agent:settings',
  '/system/staff': 'staff:manage',
};

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.UserLoginVO;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.UserLoginVO | undefined>;
  featureKeys?: string[];
}> {
  const fetchUserInfo = async () => {
    try {
      const msg = await getLogin({
        skipErrorHandler: true,
      });
      return msg.data;
    } catch (_error) {
      history.push(loginPath);
    }
    return undefined;
  };
  // 如果不是登录页面，执行
  const { location } = history;
  if (![loginPath, '/user/register', '/user/register-result'].includes(location.pathname)) {
    const currentUser = await fetchUserInfo();
    let featureKeys: string[] | undefined;
    try {
      const r = await request<any>('/manage/staff/my-features', { skipErrorHandler: true });
      featureKeys = Array.isArray(r?.data) ? r.data : (Array.isArray(r) ? r : undefined);
    } catch (_e) { /* 请求失败时 featureKeys 保持 undefined，降级不过滤 */ }
    return {
      fetchUserInfo,
      currentUser,
      featureKeys,
      settings: defaultSettings as Partial<LayoutSettings>,
    };
  }
  return {
    fetchUserInfo,
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState, setInitialState }) => {
  return {
    actionsRender: () => [<Question key="doc" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: <AvatarName />,
      render: (_, avatarChildren) => {
        return <AvatarDropdown>{avatarChildren}</AvatarDropdown>;
      },
    },
    waterMarkProps: {
      content: initialState?.currentUser?.username,
    },
    footerRender: () => <CustomFooter />,
    onPageChange: () => {
      const { location } = history;
      // 如果没有登录，重定向到 login
      if (!initialState?.currentUser && location.pathname !== loginPath) {
        history.push(loginPath);
      }
    },
    bgLayoutImgList: [
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/D2LWSqNny4sAAAAAAAAAAAAAFl94AQBr',
        left: 85,
        bottom: 100,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/C2TWRpJpiC0AAAAAAAAAAAAAFl94AQBr',
        bottom: -68,
        right: -45,
        height: '303px',
      },
      {
        src: 'https://mdn.alipayobjects.com/yuyan_qk0oxh/afts/img/F6vSTbj8KpYAAAAAAAAAAAAAFl94AQBr',
        bottom: 0,
        left: 0,
        width: '331px',
      },
    ],
    links: isDev
      ? [
          <Link key="openapi" to="/umi/plugin/openapi" target="_blank">
            <LinkOutlined />
            <span>OpenAPI 文档</span>
          </Link>,
        ]
      : [],
    menuHeaderRender: undefined,
    // 按 featureKey 过滤菜单：超管 featureKeys 含全部；staff 只有被授权的
    menuDataRender: (menuData) => {
      const fks = initialState?.featureKeys;
      // 超管/未获取到权限时不过滤
      if (!fks || fks.length === 0) return menuData;
      const fkSet = new Set(fks);
      const filter = (items: any[]): any[] =>
        items
          .map((item) => {
            const children = item.children ? filter(item.children) : [];
            const fk = PATH_FEATURE_MAP[item.path];
            const hasPermission = fk ? fkSet.has(fk) : true;
            // 有子菜单通过则保留；叶子节点按权限判断
            if (children.length > 0) return { ...item, children };
            return hasPermission ? item : null;
          })
          .filter(Boolean) as any[];
      return filter(menuData);
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return (
        <>
          {children}
          {isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((preInitialState) => ({
                  ...preInitialState,
                  settings,
                }));
              }}
            />
          )}
        </>
      );
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  ...requestConfig,
};
